import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { keyframes, useTheme } from 'styled-components';

import { LeadStatusPill } from 'app/features/leads/components/lead-status-pill';
import type { LeadSummary } from 'app/features/leads/services/gym-finder/types';
import { formatLeadInterestText } from 'app/features/leads/utils/format-lead-interest';
import Checkbox from 'app/ui/components/atoms/checkbox';
import ChevronDown from 'app/ui/components/atoms/icons/chevron-down';
import Eye from 'app/ui/components/atoms/icons/eye';
import Typography from 'app/ui/components/atoms/typography';
import { formatDateWithOrdinalMonthYear } from 'app/utils/date';

type LeadsTableProps = {
  leads: LeadSummary[];
  isLoading?: boolean;
  onViewDetails: (lead: LeadSummary) => void;
  selectedLeadIds?: ReadonlySet<string>;
  onToggleLeadSelection?: (leadId: string) => void;
  onToggleAllSelection?: () => void;
};

const NO_SELECTION: ReadonlySet<string> = new Set();

const SKELETON_ROW_COUNT = 5;
const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => index);

const TableContainer = styled.div`
  position: relative;
  width: 100%;

  ${({ theme: { media, spacing } }) => `
    ${media.medium(
      () => `
        overflow: hidden;
        border-radius: ${spacing(2)} ${spacing(2)} 0 0;
      `,
    )}
  `}
`;

const shimmer = keyframes`
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
`;

// Mobile-first: the table collapses into an accordion of cards. Each card shows the
// athlete identity plus actions, and reveals the remaining columns (Interested in,
// Applied at, Status) when expanded. At the medium breakpoint everything reverts to a
// conventional data table where every column is always visible.
const Table = styled.table<{ $isLoading: boolean }>`
  width: 100%;
  border-collapse: collapse;
  display: block;

  thead {
    position: absolute;
    width: 0.0625rem;
    height: 0.0625rem;
    padding: 0;
    margin: -0.0625rem;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }

  tbody {
    display: flex;
    flex-direction: column;
  }

  td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme: { spacing } }) => spacing(3)};
    padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(3)}`};
    vertical-align: middle;
  }

  td[data-label] {
    flex: 0 0 100%;
    order: 3;
    border-top: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
  }

  td[data-label]::before {
    content: attr(data-label);
    color: ${({ theme }) => theme.semantic.neutrals_600};
    font-family: ${({ theme: { typography } }) => typography.poppins};
    font-weight: ${({ theme: { typography } }) => typography.light};
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  ${({ theme: { media, semantic } }) => `
    ${media.medium(
      () => `
        display: table;
        table-layout: fixed;

        thead {
          position: static;
          width: auto;
          height: auto;
          margin: 0;
          overflow: visible;
          clip-path: none;
          display: table-header-group;
        }

        tbody {
          display: table-row-group;
        }

        td {
          display: table-cell;
          justify-content: initial;
          gap: 0;
          border-bottom: 0.0625rem solid ${semantic.neutrals_400};
        }

        td[data-label] {
          flex: none;
          border-top: 0;
        }

        td[data-label]::before {
          content: none;
        }

        col:nth-child(1) {
          width: 3.5rem;
        }

        col:nth-child(7) {
          width: 7.5rem;
        }
      `,
    )}
  `}

  th {
    background: ${({ theme }) => theme.semantic.neutrals_200};
    color: ${({ theme }) => theme.semantic.text_small_elements};
    font-family: ${({ theme: { typography } }) => typography.poppins};
    font-weight: ${({ theme: { typography } }) => typography.light};
    font-size: ${({ theme: { typography } }) => typography.xsmall};
    text-align: left;
    text-transform: none;
    letter-spacing: normal;
    padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(3)}`};
    border-bottom: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
    vertical-align: middle;
  }
`;

const DataRow = styled.tr<{ $isHighlighted?: boolean; $isSelected?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background: ${({ theme, $isHighlighted, $isSelected }) =>
    $isSelected ? theme.semantic.neutrals_200 : $isHighlighted ? theme.semantic.neutrals_100 : 'transparent'};
  border: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
  border-top: 0;
  border-left: ${({ theme, $isHighlighted }) =>
    $isHighlighted ? `0.1875rem solid ${theme.semantic.accent_500}` : `0.0625rem solid ${theme.semantic.neutrals_400}`};
  border-radius: ${({ theme: { spacing } }) => spacing(0)};
  overflow: hidden;

  &:first-child {
    border-top: 0.0625rem solid ${({ theme }) => theme.semantic.neutrals_400};
  }

  &[data-expanded='false'] td[data-label] {
    display: none;
  }

  ${({ theme: { media, semantic } }) => `
    ${media.medium(
      () => `
        display: table-row;
        border: 0;
        border-radius: 0;
        overflow: visible;

        &:first-child {
          border-top: 0.0625rem solid ${semantic.neutrals_400};
        }

        &:last-child {
          border-bottom: 0.0625rem solid ${semantic.neutrals_400};
        }

        &[data-expanded='false'] td[data-label] {
          display: table-cell;
        }
      `,
    )}
  `}
`;

const AthleteCell = styled.td<{ $isHighlighted?: boolean }>`
  flex: 1 1 auto;
  order: 1;
  min-width: 0;

  ${({ theme: { media, semantic }, $isHighlighted }) => `
    ${media.medium(
      () => `
        border-left: ${$isHighlighted ? `0.0625rem solid ${semantic.accent_500}` : 'transparent'};
      `,
    )}
  `}
`;

const SelectCell = styled.td`
  flex: 0 0 auto;
  order: 0;
`;

export const AthleteIdentity = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const AthleteEmail = styled.span`
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-weight: ${({ theme: { typography } }) => typography.light};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.semantic.neutrals_600};
  line-height: ${({ theme: { typography } }) => typography.lineHeight};
`;

const CellText = styled(Typography)`
  font-size: ${({ theme: { typography } }) => typography.tiny};
  letter-spacing: normal;
`;

export const AthleteName = styled(Typography)`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionsCell = styled.td`
  flex: 0 0 auto;
  order: 2;
  justify-content: flex-end;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  white-space: nowrap;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        text-align: right;
      `,
    )}
  `}
`;

const ViewDetailsButton = styled.button`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  text-decoration: none;
  color: ${({ theme }) => theme.semantic.accent_500};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.xsmall};
  font-weight: ${({ theme: { typography } }) => typography.light};

  &:hover {
    color: ${({ theme }) => theme.semantic.accent_700};
  }

  &:focus-visible {
    outline: ${({ theme: { spacing } }) => spacing(0.5)} solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: ${({ theme: { spacing } }) => spacing(0.5)};
  }
`;

// The eye icon stands in for "View details" on mobile; the full text label takes over
// from the medium breakpoint where there is room in the actions column.
const ViewDetailsIcon = styled.span`
  display: inline-flex;
  align-items: center;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        display: none;
      `,
    )}
  `}
`;

const ViewDetailsLabel = styled.span`
  display: none;

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        display: inline;
      `,
    )}
  `}
`;

const ToggleButton = styled.button`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.semantic.neutrals_200};
  color: ${({ theme }) => theme.semantic.text_small_elements};

  &:focus-visible {
    outline: ${({ theme: { spacing } }) => spacing(0.5)} solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: ${({ theme: { spacing } }) => spacing(0.5)};
  }

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        display: none;
      `,
    )}
  `}
`;

const ChevronWrapper = styled.span<{ $isExpanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-in-out;
  transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const EmptyRow = styled.tr`
  td {
    justify-content: center;
    text-align: center;
    color: ${({ theme }) => theme.semantic.neutrals_600};
  }
`;

const LoadingStatus = styled.div.attrs({ role: 'status' })`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

const SkeletonLine = styled.span<{ $width: string; $height?: string }>`
  display: block;
  width: ${({ $width }) => $width};
  max-width: 100%;
  height: ${({ $height = '1rem' }) => $height};
  border-radius: ${({ theme: { spacing } }) => spacing(4)};
  background: ${({ theme }) =>
    `linear-gradient(90deg, ${theme.semantic.neutrals_300} 25%, ${theme.semantic.neutrals_400} 37%, ${theme.semantic.neutrals_300} 63%)`};
  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const SkeletonIdentity = styled(AthleteIdentity)`
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

export const LeadsTable = ({
  leads,
  isLoading = false,
  onViewDetails,
  selectedLeadIds = NO_SELECTION,
  onToggleLeadSelection,
  onToggleAllSelection,
}: LeadsTableProps) => {
  const intl = useIntl();
  const { semantic } = useTheme();

  // Single-open accordion on mobile: expanding one card collapses the others. Desktop
  // ignores this state because every column is rendered as a regular table cell there.
  const [expandedLeadId, setExpandedLeadId] = useState<string | null>(null);

  const handleToggle = (leadId: string) => {
    setExpandedLeadId((current) => (current === leadId ? null : leadId));
  };

  const selectedVisibleCount = leads.filter((lead) => selectedLeadIds.has(lead.id)).length;
  const areAllSelected = leads.length > 0 && selectedVisibleCount === leads.length;
  const areSomeSelected = selectedVisibleCount > 0 && !areAllSelected;

  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = areSomeSelected;
    }
  }, [areSomeSelected]);

  const headers = {
    athlete: intl.formatMessage({ id: 'leads.table.athlete' }),
    interestedIn: intl.formatMessage({ id: 'leads.table.interestedIn' }),
    receivedAt: intl.formatMessage({ id: 'leads.table.receivedAt' }),
    status: intl.formatMessage({ id: 'leads.table.status' }),
    events: intl.formatMessage({ id: 'leads.table.events' }),
    actions: intl.formatMessage({ id: 'leads.table.actions' }),
  };
  const loadingLabel = intl.formatMessage({
    id: 'leads.table.loading',
    defaultMessage: 'Loading leads',
  });

  return (
    <TableContainer>
      {isLoading ? (
        <LoadingStatus aria-label={loadingLabel} aria-live="polite">
          {loadingLabel}
        </LoadingStatus>
      ) : null}
      <Table
        aria-label={intl.formatMessage({ id: 'leads.table.ariaLabel' })}
        aria-busy={isLoading}
        data-testid="leads-table"
        data-loading={isLoading}
        $isLoading={isLoading}
      >
        <colgroup>
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">
              <Checkbox
                ref={selectAllRef}
                aria-label={intl.formatMessage({ id: 'leads.table.selectAll' })}
                data-testid="leads-select-all"
                checked={areAllSelected}
                disabled={isLoading || leads.length === 0}
                onChange={() => onToggleAllSelection?.()}
              />
            </th>
            <th scope="col">{headers.athlete}</th>
            <th scope="col">{headers.interestedIn}</th>
            <th scope="col">{headers.receivedAt}</th>
            <th scope="col">{headers.status}</th>
            <th scope="col">{headers.events}</th>
            <th scope="col">
              <span className="visually-hidden">{headers.actions}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            skeletonRows.map((row) => (
              <DataRow
                key={row}
                aria-hidden="true"
                data-testid={`leads-skeleton-row-${row}`}
                data-expanded={true}
                data-highlighted={false}
                $isHighlighted={false}
              >
                <SelectCell>
                  <SkeletonLine $width="1.25rem" $height="1.25rem" />
                </SelectCell>
                <AthleteCell>
                  <SkeletonIdentity>
                    <SkeletonLine $width="9rem" />
                    <SkeletonLine $width="13rem" $height="0.75rem" />
                  </SkeletonIdentity>
                </AthleteCell>
                <td data-label={headers.interestedIn}>
                  <SkeletonLine $width="8rem" />
                </td>
                <td data-label={headers.receivedAt}>
                  <SkeletonLine $width="7rem" />
                </td>
                <td data-label={headers.status}>
                  <SkeletonLine $width="5rem" $height="1.5rem" />
                </td>
                <td data-label={headers.events}>
                  <SkeletonLine $width="8rem" />
                </td>
                <ActionsCell>
                  <SkeletonLine $width="5.5rem" />
                </ActionsCell>
              </DataRow>
            ))
          ) : leads.length === 0 ? (
            <EmptyRow data-testid="leads-table-empty">
              <td colSpan={7}>
                <Typography variant="body2">{intl.formatMessage({ id: 'leads.table.empty' })}</Typography>
              </td>
            </EmptyRow>
          ) : (
            leads.map((lead) => {
              const isExpanded = expandedLeadId === lead.id;
              const isHighlighted = lead.status === 'new';
              const isSelected = selectedLeadIds.has(lead.id);

              return (
                <DataRow
                  key={lead.id}
                  data-testid={`leads-row-${lead.id}`}
                  data-expanded={isExpanded}
                  data-highlighted={isHighlighted}
                  data-selected={isSelected}
                  $isHighlighted={isHighlighted}
                  $isSelected={isSelected}
                >
                  <SelectCell>
                    <Checkbox
                      aria-label={intl.formatMessage(
                        { id: 'leads.table.selectLead' },
                        { athleteName: lead.athleteName },
                      )}
                      data-testid={`leads-select-${lead.id}`}
                      checked={isSelected}
                      onChange={() => onToggleLeadSelection?.(lead.id)}
                    />
                  </SelectCell>
                  <AthleteCell $isHighlighted={isHighlighted}>
                    <AthleteIdentity>
                      <AthleteName variant="body3">{lead.athleteName}</AthleteName>
                      <AthleteEmail>{lead.athleteEmail}</AthleteEmail>
                    </AthleteIdentity>
                  </AthleteCell>
                  <td data-label={headers.interestedIn}>
                    <CellText variant="body3">{formatLeadInterestText(lead.enquiryType)}</CellText>
                  </td>
                  <td data-label={headers.receivedAt}>
                    <CellText variant="body3">{formatDateWithOrdinalMonthYear(lead.receivedAt, intl.locale)}</CellText>
                  </td>
                  <td data-label={headers.status}>
                    <LeadStatusPill status={lead.status} />
                  </td>
                  <td data-label={headers.events}>
                    <CellText variant="body3">{lead.events.join(', ')}</CellText>
                  </td>
                  <ActionsCell>
                    <ViewDetailsButton
                      type="button"
                      aria-label={intl.formatMessage(
                        { id: 'leads.table.viewDetailsFor' },
                        { athleteName: lead.athleteName },
                      )}
                      data-testid={`leads-view-details-${lead.id}`}
                      onClick={() => onViewDetails(lead)}
                    >
                      <ViewDetailsLabel>{intl.formatMessage({ id: 'leads.table.viewDetails' })}</ViewDetailsLabel>
                      <ViewDetailsIcon>
                        <Eye />
                      </ViewDetailsIcon>
                    </ViewDetailsButton>
                    <ToggleButton
                      type="button"
                      aria-expanded={isExpanded}
                      aria-label={intl.formatMessage(
                        {
                          id: 'leads.table.toggleDetails',
                          defaultMessage: 'Show or hide details for {athleteName}',
                        },
                        { athleteName: lead.athleteName },
                      )}
                      data-testid={`leads-row-toggle-${lead.id}`}
                      onClick={() => handleToggle(lead.id)}
                    >
                      <ChevronWrapper $isExpanded={isExpanded}>
                        <ChevronDown color={semantic.text_small_elements} />
                      </ChevronWrapper>
                    </ToggleButton>
                  </ActionsCell>
                </DataRow>
              );
            })
          )}
        </tbody>
      </Table>
    </TableContainer>
  );
};

export default LeadsTable;
